import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Tag } from "lucide-react";
import { VN_TIME_ZONE, labelTexts, normalizeLabelSize, type LabelContent } from "@hisense/shared";
import { labelBarcodeFits } from "@/lib/labelDocument";
import { LabelPreview } from "@/components/products/LabelPreview";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { useSettings } from "@/api/hooks";
import { LabelLanguageToggle, useLabelLanguage } from "@/components/domain/LabelLanguageToggle";
import {
  printLabel,
} from "@/lib/labelPrinter";

/**
 * Nút in tem nhiệt, dùng chung cho Sản phẩm / Phiếu mẫu / Đơn mẫu.
 *
 * MỘT component cho cả ba chỗ, vì hệ thống cũ có ba bản sao và chúng đã trôi
 * khỏi nhau (ví dụ ở Sample Order chỉ in được dòng hàng ĐẦU TIÊN của đơn).
 *
 * Hộp thoại hỏi hai thứ hệ cũ cũng hỏi trước khi in: SỐ TEM và số lượng mẫu ghi
 * đè — không hỏi thì in một tem rồi phải bấm lại chín lần.
 *
 * Khổ tem đọc từ Cài đặt. Khổ sai thì NÓI RA và không in: in cả xấp tem lệch
 * khổ rồi mới phát hiện là hỏng vật tư thật.
 */
/**
 * Mã in trên tem: "mã sản phẩm / mã phụ", đúng như hệ thống cũ ghép
 * (ProductData.js:1558, SampleCard.js:951, SampleOrder.js:2479).
 *
 * VÌ SAO KHÔNG BỎ ĐƯỢC MÃ PHỤ: trên dữ liệu thật, 667 sản phẩm thuộc 155 nhóm
 * DÙNG CHUNG mã sản phẩm và chỉ khác nhau ở mã phụ. Bỏ mã phụ đi thì dây rút
 * 140cm, 145cm và 150cm in ra ba tem giống hệt nhau từng chữ, kể cả mã vạch —
 * dán lên ba túi hàng là mất khả năng phân biệt vĩnh viễn, và sai sót đó đi
 * thẳng ra khỏi kho.
 */
export function labelRef(productRef: string, subRef?: string | null): string {
  const sub = subRef?.trim();
  return sub ? `${productRef} / ${sub}` : productRef;
}

export function LabelPrintButton({
  productRef,
  subRef,
  description,
  descriptionZh,
  color,
  colorZh,
  size,
  lotNumber,
  barcode,
  quantity,
  compact,
}: {
  /** Trường (1) của tem — in trần, không nhãn. */
  productRef: string;
  /** Mã phụ. Ghép vào trường (1) và vào mã vạch — xem labelRef. */
  subRef?: string | null;
  description?: string | null;
  /** Truyền (kể cả null) khi bản ghi CÓ trường tiếng Hoa — hộp thoại mới hiện ô chọn ngôn ngữ trên tem. */
  descriptionZh?: string | null;
  color?: string | null;
  colorZh?: string | null;
  size?: string | null;
  /** Bổ sung của hệ mới; in sau 5 trường cũ và chỉ khi còn chỗ. */
  lotNumber?: string | null;
  barcode?: string | null;
  /** Số lượng mẫu in lên tem; người dùng ghi đè được ngay lúc in. */
  quantity?: number | null;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const { data: settings } = useSettings();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState("1");
  const [qty, setQty] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [language, setLanguage] = useLabelLanguage();
  // Bản ghi chỉ có một ô mô tả (phiếu mẫu) thì không có gì để chọn: in như trước, nhãn tiếng Anh.
  const bilingual = descriptionZh !== undefined || colorZh !== undefined;
  const texts = labelTexts({ description, descriptionZh, color, colorZh }, bilingual ? language : "en");

  const sized = normalizeLabelSize(settings?.labelPrinter ?? {});
  const sizeError = "invalid" in sized ? sized.invalid : null;
  const labelSize = "size" in sized ? sized.size : null;

  const run = () => {
    if (!labelSize) return;
    if (!labelBarcodeFits(labelSize, barcode ? labelRef(barcode, subRef) : null)) { setMsg(t("productWorkspace.labelUnavailable")); return; }
    if (!/^\d+$/.test(count) || Number(count) < 1 || Number(count) > 99) { setMsg(t("productWorkspace.invalidLabelCount")); return; }
    setMsg(null);
    const ref = labelRef(productRef, subRef);
    const content: LabelContent = {
      productRef: ref,
      ...texts,
      size,
      // Ô ghi đè thắng giá trị của bản ghi; để trống thì dùng số lượng thật.
      sampleQty: qty.trim() || (quantity != null ? String(quantity) : null),
      lotNumber,
      // Mã vạch cũng phải mang mã phụ, nếu không máy quét vẫn không phân biệt được.
      barcode: barcode ? labelRef(barcode, subRef) : null,
      /**
       * Chân tem của hệ cũ: tên công ty và NGÀY IN.
       *
       * Ngày in là thứ duy nhất trên tem cho biết mẫu được dán ở đợt nào — hai
       * mẫu cùng mã, khác lần gửi, chỉ phân biệt được nhờ nó. Dựng ở đây chứ
       * không trong buildTspl để bộ dựng lệnh còn THUẦN và kiểm thử được.
       *
       * Dùng en-GB để ra dạng DD/MM/YYYY đúng hệ cũ. (Trước 20/09/2026 dòng
       * này còn phải giữ ASCII để đi lọt đường in USB; đường đó đã bỏ, nhưng
       * định dạng ngày thì giữ nguyên vì nhân viên đọc quen.)
       */
      footer: `Hisense Button  ${new Date().toLocaleDateString("en-GB", { timeZone: VN_TIME_ZONE })}`,
      count: Number(count),
    };
    printLabel(labelSize, content);
    setOpen(false);
  };

  return (
    <>
      <Button
        variant="ghost"
        size={compact ? "icon-sm" : "sm"}
        onClick={(e) => {
          e.stopPropagation();
          // Xoá ô ghi đè số lượng mỗi lần mở lại. Giữ lại giá trị cũ nghĩa là
          // in tem sản phẩm KẾ TIẾP với số lượng của sản phẩm trước — sai lặng
          // lẽ, và sai trên vật tư đã in ra rồi.
          setQty("");
          setMsg(null);
          setOpen(true);
        }}
        aria-label={`${t("label.print")}: ${labelRef(productRef, subRef)}`}
        title={`${t("label.print")}: ${labelRef(productRef, subRef)}`}
      >
        <Tag className="size-3.5" />
        {compact ? null : t("label.print")}
      </Button>

      {open && (
        <Modal
          eyebrow={t("label.printer")}
          title={`${t("label.print")} · ${labelRef(productRef, subRef)}`}
          closeLabel={t("common.close")}
          onClose={() => setOpen(false)}
        >
          <div className="min-h-0 space-y-3 overflow-y-auto p-4">
            {sizeError ? (
              <p className="rounded-sm bg-danger-tint px-3 py-2 text-xs text-danger">
                {t("label.error.badSize", { field: sizeError })}
              </p>
            ) : (
              <p className="text-2xs text-ink-faint">
                {t("label.sizeInUse", {
                  w: labelSize?.widthMm,
                  h: labelSize?.heightMm,
                })}
              </p>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <FieldLabel htmlFor="lp-count">{t("label.count")}</FieldLabel>
                <Input
                  id="lp-count"
                  inputMode="numeric"
                  value={count}
                  onChange={(e) => setCount(e.target.value)}
                />
              </div>
              <div>
                <FieldLabel htmlFor="lp-qty" hint={t("label.qtyHint")}>
                  {t("label.qty")}
                </FieldLabel>
                <Input
                  id="lp-qty"
                  inputMode="numeric"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  placeholder={quantity != null ? String(quantity) : undefined}
                />
              </div>
            </div>

            {bilingual && <LabelLanguageToggle value={language} onChange={setLanguage} />}

            {labelSize && <LabelPreview size={labelSize} content={{
              productRef: labelRef(productRef, subRef), ...texts, size,
              sampleQty: qty.trim() || (quantity != null ? String(quantity) : null), lotNumber,
              barcode: barcode ? labelRef(barcode, subRef) : null, count: 1,
              footer: `Hisense Button  ${new Date().toLocaleDateString("en-GB", { timeZone: VN_TIME_ZONE })}`,
            }} />}
            {/* Chỗ duy nhất việc in hỏng được mà không phải lỗi của phần mềm:
                máy tem chưa được cài trong máy tính thì nó không có trong hộp
                thoại in, và tem chui ra máy in giấy A4. Nói trước, đừng để
                người dùng tự đoán. */}
            <p className="text-2xs text-ink-faint">{t("label.dialogHint")}</p>

            {msg && (
              <p className="rounded-sm bg-sunken px-3 py-2 text-xs text-ink-soft">{msg}</p>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <div className="flex gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
                  {t("common.cancel")}
                </Button>
                <Button type="button" size="sm" disabled={!!sizeError || !!labelSize && !labelBarcodeFits(labelSize, barcode ? labelRef(barcode, subRef) : null)} onClick={run}>
                  {t("label.print")}
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
